import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import Modal from '../components/Modal';
import Tooltip from '../components/Tooltip';
import PreviewModal from '../components/PreviewModal';

function Mailings() {
  const [mailings, setMailings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [editingMailing, setEditingMailing] = useState(null);
  const [classes, setClasses] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  const [pendingMailingId, setPendingMailingId] = useState(null);
  const [sortField, setSortField] = useState('created_at'); // 'created_at' или 'started_at'
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' = новые сверху, 'asc' = старые сверху
  const [stats, setStats] = useState({
  total_users: 0,
  telegram_users_active_new: 0,
  by_class: {}
  });

  // ✅ fetchStats функция
  const fetchStats = async () => {
    try {
      const data = await api.getMailingStats();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const virtualClasses = useMemo(() => [
    // { id: -1, name: 'Всем Telegram (активные)', count: stats.telegram_users_active_new },
    { id: 0, name: 'Без интереса', count: stats.by_class['0']?.count || 0 },
    { id: 'all', name: 'Все пользователи', count: stats.total_users }
  ], [stats]);

  // Функция для русификации ошибок Telegram
  const translateError = (error) => {
    const errorLower = error.toLowerCase();
    if (errorLower.includes('chat not found')) return 'Чат не найден';
    if (errorLower.includes('user not found')) return 'Пользователь не найден';
    if (errorLower.includes('bot was blocked')) return 'Бот был заблокирован';
    if (errorLower.includes('blocked')) return 'Бот заблокирован пользователем';
    if (errorLower.includes('forbidden')) return 'Доступ запрещён';
    if (errorLower.includes('user is deactivated')) return 'Пользователь деактивирован';
    if (errorLower.includes('chat_write_forbidden')) return 'Запрещена запись в чат';
    if (errorLower.includes('too many requests')) return 'Слишком много запросов';
    if (errorLower.includes('retry after')) return 'Повторите позже';
    if (errorLower.includes('no module named')) return 'Модуль не найден: ' + error.split("'")[1];
    if (errorLower.includes("'int' object has no attribute")) return 'Ошибка сервера: неверный тип данных';
    if (errorLower.includes('status')) return 'Ошибка статуса ответа';
    if (errorLower.includes('timeout')) return 'Превышено время ожидания';
    if (errorLower.includes('connection')) return 'Ошибка соединения';
    if (errorLower.includes('network')) return 'Ошибка сети';
    if (errorLower.includes('bad request')) return 'Неверный запрос';
    if (errorLower.includes('internal server error')) return 'Внутренняя ошибка сервера';
    if (errorLower.includes('not found')) return 'Не найдено';
    if (errorLower.includes('unauthorized')) return 'Не авторизован';
    if (errorLower.includes('flood')) return 'Слишком частые запросы';
    if (errorLower.includes('have no rights')) return 'Нет прав для отправки';
    if (errorLower.includes('kicked')) return 'Пользователь удалён из чата';
    if (errorLower.includes('not enough rights')) return 'Недостаточно прав';
    if (errorLower.includes('peer_id_invalid')) return 'Неверный ID пользователя';
    if (errorLower.includes('input_user_deactivated')) return 'Аккаунт пользователя удалён';
    return error; // Если нет перевода, возвращаем оригинал
  };

  const [formData, setFormData] = useState({
    name: '',
    message: '',
    image: null,
    imageUrl: null,
    imageName: '',
    selectedClasses: [],
    action: 'draft', // draft, now, scheduled
    scheduledDate: ''
  });

  useEffect(() => {
    loadMailings();
    fetchClasses();
    fetchStats();
  }, []);

  // ✅ Автообновление рассылок каждые 7 секунд
  useEffect(() => {
    const interval = setInterval(() => {
      loadMailings();
      fetchStats();
    }, 7000);

    return () => clearInterval(interval);
  }, []);

  const loadMailings = async () => {
    try {
      setLoading(true);
      const data = await api.getMailings();
      setMailings(data);
    } catch (error) {
      console.error('Error fetching mailings:', error);
      toast.error('Ошибка загрузки рассылок');
    } finally {
      setLoading(false);
    }
  };

  const fetchClasses = async () => {
    try {
      const data = await api.getClasses();
      setClasses(data);
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const calculateRecipients = (selectedClassIds) => {
    if (selectedClassIds.length === 0) return stats.total_users;

    // Без интереса [0]
    if (selectedClassIds.includes(0)) return stats.by_class['0']?.count || 0;

    // Все пользователи
    if (selectedClassIds.includes('all')) return stats.total_users;

    // Конкретные классы
    return selectedClassIds
      .filter(id => typeof id === 'number' && id > 0)
      .reduce((sum, id) => sum + (stats.by_class[id]?.count || 0), 0);
  };

  const recipientsCount = calculateRecipients(formData.selectedClasses);

  const normalizeTargetClasses = (selectedClasses) => {
    let targetClasses = [...selectedClasses];
    if (targetClasses.includes(0)) {
      targetClasses = [0];
    } else if (targetClasses.includes('all')) {
      targetClasses = [];
    } else {
      targetClasses = targetClasses.filter(id => typeof id === 'number' && id > 0);
    }
    return targetClasses;
  };

  const handleClassToggle = (classId) => {
    setFormData(prev => ({
      ...prev,
      selectedClasses: prev.selectedClasses.includes(classId)
        ? prev.selectedClasses.filter(id => id !== classId)
        : [...prev.selectedClasses, classId]
    }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Проверка формата
      const allowedFormats = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedFormats.includes(file.type)) {
        toast.error('Допустимые форматы: JPG, JPEG, PNG, GIF, WEBP');
        e.target.value = '';
        return;
      }

      // Проверка размера (10 МБ = 10 * 1024 * 1024 байт)
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error('Размер файла не должен превышать 10 МБ');
        e.target.value = '';
        return;
      }

      // Создаем URL для предпросмотра
      const imageUrl = URL.createObjectURL(file);
      // Сохраняем и файл, и URL для предпросмотра
      setFormData(prev => ({
        ...prev,
        image: file,
        imageUrl: imageUrl,
        imageName: file.name
      }));
      toast.success('Изображение загружено');
    }
  };

  const saveMailing = async (status) => {
    try {
    const targetClasses = normalizeTargetClasses(formData.selectedClasses);

    const mailingData = {
      name: formData.name,
      message: formData.message,
      target_classes: targetClasses,  // ✅ Правильный формат!
      status: status,
      scheduled_at: formData.action === 'scheduled' ? `${formData.scheduledDate}:00Z` : null,
    };
      console.log('🔍 DEBUG API mailingData=', mailingData);

        let savedMailing;
        let mailingId = null;

      if (editingMailing) {
        savedMailing = await api.updateMailing(editingMailing.id, mailingData);
        toast.success('Рассылка обновлена');
        mailingId = editingMailing.id;
      } else {
        savedMailing = await api.createMailing(mailingData);
        mailingId = savedMailing.id;
        if (status === 'draft') {
          toast.success('Рассылка создана');
        }
      }

      if (formData.image instanceof File && savedMailing?.id) {
        const form = new FormData();
        form.append('image', formData.image);
        await api.uploadMailingImage(savedMailing.id, form);
      }

      loadMailings();

      if (status === 'draft') {
        closeModal();
      }

      return mailingId;
    } catch (error) {
      console.error('Error saving mailing:', error);
      toast.error('Ошибка при сохранении рассылки');
      return null;
      }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('Введите название рассылки');
      return;
    }
    if (!formData.message.trim()) {
      toast.error('Введите текст сообщения');
      return;
    }
    if (formData.selectedClasses.length === 0) {
      toast.error('Выберите хотя бы один интерес');
      return;
    }
    if (formData.action === 'scheduled' && !formData.scheduledDate) {
      toast.error('Выберите дату и время запуска');
      return;
    }
    if (formData.action === 'scheduled' && new Date(`${formData.scheduledDate}:00Z`) <= new Date()) {
      toast.error('Нельзя запланировать рассылку на прошедшее время');
      return;
    }

    // Если это черновик, сохраняем сразу без preview
    if (formData.action === 'draft') {
      await saveMailing('draft');
      return;
    }

    // Для "Запустить сейчас" и "Запланировать" показываем preview
    const normalizedTargetClasses = normalizeTargetClasses(formData.selectedClasses);
    let exactRecipientsCount = recipientsCount;
    try {
      const countData = await api.getMailingRecipientsCount(normalizedTargetClasses);
      exactRecipientsCount = countData?.recipients_count ?? recipientsCount;
    } catch (error) {
      console.error('Error fetching exact recipients count:', error);
    }

    const targetClassesNames = formData.selectedClasses
      .map(classId => getClassName(classId))
      .join(', ');

    setPreviewData({
      name: formData.name,
      message: formData.message,
      image: formData.imageUrl || formData.image,
      imageName: formData.imageName,
      imageFile: formData.image instanceof File ? formData.image : null,
      recipientsCount: exactRecipientsCount,
      targetClassesNames: targetClassesNames,
      targetClasses: formData.selectedClasses.filter(id => typeof id === 'number'), // Для создания временного черновика
      isNew: !editingMailing, // Флаг новой рассылки
    });

    setShowModal(false); // Скрываем первую модалку
    setShowPreviewModal(true);
  };

  const handlePreviewConfirm = async () => {
    setShowPreviewModal(false);

    // ✅ 1. SCHEDULED: запланировать на будущее время (БЕЗ 30сек таймера!)
    if (formData.action === 'scheduled') {
      try {
        // Подготавливаем данные для scheduled рассылки
        const targetClasses = normalizeTargetClasses(formData.selectedClasses);

        const mailingData = {
          name: formData.name,
          message: formData.message,
          target_classes: targetClasses,
          status: 'scheduled',                    // ✅ status="scheduled"!
          scheduled_at: `${formData.scheduledDate}:00Z`,   // ✅ Время трактуем как UTC (бэк работает в UTC+0)
        };

        let savedMailing;
        if (editingMailing) {
          savedMailing = await api.updateMailing(editingMailing.id, mailingData);
        } else {
          savedMailing = await api.createMailing(mailingData);
          // Загружаем изображение если есть
          if (formData.image instanceof File && savedMailing?.id) {
            const form = new FormData();
            form.append('image', formData.image);
            await api.uploadMailingImage(savedMailing.id, form);
          }
        }

        toast.success(`✅ Запланировано на ${new Date(formData.scheduledDate).toLocaleString('ru-RU')}`);
        loadMailings();
        closeModal();
      } catch (error) {
        console.error('Error creating scheduled mailing:', error);
        toast.error('Ошибка при планировании рассылки');
      }
      return; // ← КРИТИЧНО! Без 30сек таймера!
    }

    // ✅ 2. Если это запуск существующего черновика из таблицы
    if (pendingMailingId) {
      try {
        await launchMailing(pendingMailingId);
        showLaunchNotification(
          () => api.delayedStartCommitMailing(pendingMailingId),
          () => cancelMailing(pendingMailingId)
        );
      } finally {
        setPendingMailingId(null);
      }
      return;
    }

    // ✅ 3. "Запустить сейчас" - новая/редактируемая рассылка (30сек таймер)
    try {
      const mailingId = editingMailing 
        ? await saveMailing('draft') 
        : await saveMailing('draft');

      if (mailingId) {
        await launchMailing(mailingId);
        showLaunchNotification(
          () => api.delayedStartCommitMailing(mailingId),
          () => cancelMailing(mailingId)
        );
      }
    } catch (error) {
      console.error('Error launching mailing:', error);
      toast.error('Ошибка при запуске рассылки');
    }
};

  const handlePreviewCancel = () => {
    setShowPreviewModal(false);
    setShowModal(true); // Возвращаем модалку редактирования
  };

  const showLaunchNotification = (onConfirm, onCancelCallback) => {
    let cancelled = false;

    const LaunchToast = ({ onCancel }) => {
      const [seconds, setSeconds] = React.useState(30);
      const [progress, setProgress] = React.useState(100);

      React.useEffect(() => {
        const interval = setInterval(() => {
          setSeconds(prev => {
            const newSeconds = prev - 1;
            if (newSeconds <= 0) {
              clearInterval(interval);
            }
            setProgress((newSeconds / 30) * 100);
            return newSeconds;
          });
        }, 1000);

        return () => clearInterval(interval);
      }, []);

      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '280px' }}>
          <div style={{ fontSize: '14px', fontWeight: '500' }}>
            Запуск через {seconds} сек
          </div>
          <div style={{
            width: '100%',
            height: '4px',
            background: '#e5e7eb',
            borderRadius: '2px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${progress}%`,
              height: '100%',
              background: '#3b82f6',
              transition: 'width 1s linear',
              borderRadius: '2px'
            }} />
          </div>
          <button
            onClick={onCancel}
            style={{
              padding: '6px 12px',
              background: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '13px',
            }}
          >
            Отменить
          </button>
        </div>
      );
    };

    const toastId = toast(
      (t) => (
        <LaunchToast
          onCancel={() => {
            cancelled = true;
            toast.dismiss(t.id);
            toast.success('Рассылка отменена');
            if (onCancelCallback) {
              onCancelCallback();
            }
          }}
        />
      ),
      {
        duration: Infinity,
        position: 'top-right',
      }
    );

    // Через 30 секунд — коммит запуска (если не отменили)
    setTimeout(async () => {
      if (!cancelled) {
        toast.dismiss(toastId);
        try {
          await onConfirm(); // здесь мы будем звать delayed_start_commit
        } catch (e) {
          // onConfirm сам покажет ошибку, если нужно
        }
      }
    }, 30000);
  };

  const handleEdit = (mailing) => {
    setEditingMailing(mailing);
    setFormData({
      name: mailing.name,
      message: mailing.message,
      image: null, // новый аплоад будет File, старую картинку просто показывай отдельно, если нужно
      selectedClasses: mailing.target_classes.map(id => Number(id)),
      action:
        mailing.status === 'scheduled'
          ? 'scheduled'
          : mailing.status === 'draft'
          ? 'draft'
          : 'draft',
      scheduledDate: mailing.scheduled_at
        ? (() => {
            const d = new Date(mailing.scheduled_at);
            const pad = (n) => String(n).padStart(2, '0');
            return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
          })()
        : ''
    });
    setShowModal(true);
  };

  const handleLaunch = async (mailingId) => {
    // Находим рассылку для предпросмотра
    const mailing = mailings.find(m => m.id === mailingId);
    if (!mailing) return;

    const targetClassesNames = mailing.target_classes
      .map(classId => getClassName(classId))
      .join(', ');

    setPreviewData({
      name: mailing.name,
      message: mailing.message,
      image: mailing.image,
      imageName: null,
      imageFile: null,
      recipientsCount: mailing.recipients_count,
      targetClassesNames: targetClassesNames,
      isNew: false, // Существующая рассылка
    });

    setPendingMailingId(mailingId);
    setShowPreviewModal(true);
  };

  const launchMailing = async (mailingId) => {
    try {
      await api.delayedStartMailing(mailingId);    // 🔹 теперь отложенный старт
      toast.success('Рассылка будет запущена через 30 секунд');
      loadMailings();
      closeModal();
    } catch (error) {
      console.error('Error launching mailing:', error);
      toast.error('Ошибка при запуске рассылки');
      throw error;
    }
  };


  const cancelMailing = async (mailingId) => {
    try {
      const res = await api.cancelStartMailing(mailingId);
      console.log('cancelStart OK', res);
      // Пока без success-тоста, чтобы не путал при 400
      loadMailings();
      closeModal();
    } catch (error) {
      console.error('Error cancelling mailing:', error);
      const detail = error?.response?.data?.detail || 'Ошибка при отмене рассылки';
      toast.error(detail);
    }
  };

  const handleDelete = async (mailingId) => {
    if (window.confirm('Вы уверены, что хотите удалить эту рассылку?')) {
      try {
        await api.deleteMailing(mailingId);
        toast.success('Рассылка удалена');
        loadMailings();
      } catch (error) {
        console.error('Error deleting mailing:', error);
        toast.error('Ошибка при удалении рассылки');
      }
    }
  };

  const loadExampleMailing = async () => {
    try {
      const testMailing = {
        name: 'Приветственная рассылка (пример)',
        message: 'Добро пожаловать! Это тестовая рассылка для демонстрации функционала.',
        image: 'welcome.jpg',
        target_classes: classes.length > 0 ? [classes[0].id, classes[1]?.id].filter(Boolean) : [1, 2],
        status: 'active',
        scheduled_at: null,
      };

      await api.createMailing(testMailing);
      toast.success('Пример рассылки добавлен');
      loadMailings();
    } catch (error) {
      console.error('Error creating example mailing:', error);
      toast.error('Ошибка при создании примера');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingMailing(null);
    // Освобождаем URL объекта при закрытии модалки
    if (formData.imageUrl) {
      URL.revokeObjectURL(formData.imageUrl);
    }
    setFormData({
      name: '',
      message: '',
      image: null,
      imageUrl: null,
      imageName: '',
      selectedClasses: [],
      action: 'draft',
      scheduledDate: ''
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: { label: 'Черновик', class: 'status-pending' },
      pending: { label: 'Ожидание запуска', class: 'status-in-progress' },
      scheduled: { label: 'Запланирована', class: 'status-in-progress' },
      active: { label: 'Выполняется', class: 'status-in-progress' },
      completed: { label: 'Завершена', class: 'status-completed' },
      cancelled: { label: 'Отменена', class: 'status-cancelled' },
      error: { label: 'Ошибка', class: 'status-cancelled' }
    };
    const badge = badges[status] || badges.draft;
    return <span className={`status-badge ${badge.class}`}>{badge.label}</span>;
  };

  const getClassName = (classId) => {

    // if (classId === -1) return 'Всем Telegram';
    if (classId === 0) return 'Без интереса';
    if (classId === 'all') return 'Все пользователи';

    const classObj = classes.find(c => c.id === classId);
    return classObj ? classObj.name : 'Неизвестно';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const hasActiveMailings = mailings.some(m =>
    m.status === 'active' || m.status === 'pending'
  );

  const sortedMailings = useMemo(() => {
    return [...mailings].sort((a, b) => {
      let dateA, dateB;
      if (sortField === 'started_at') {
        dateA = new Date(a.started_at || a.scheduled_at || 0);
        dateB = new Date(b.started_at || b.scheduled_at || 0);
      } else {
        dateA = new Date(a.created_at);
        dateB = new Date(b.created_at);
      }
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });
  }, [mailings, sortField, sortOrder]);

  return (
    <div className="page">
      <div className="page-header">
        <h2>
          Рассылки
          {hasActiveMailings && (
            <span style={{
              fontSize: '12px',
              color: '#10b981',
              marginLeft: '10px',
              fontWeight: 'normal'
            }}>
              ● Автообновление
            </span>
          )}
        </h2>
        <div className="action-buttons">
          <button
            onClick={() => setShowModal(true)}
            className="btn btn-primary"
          >
            Создать рассылку
          </button>
        </div>
      </div>

      {loading ? (
        <div className="table-container">
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-500">Загрузка рассылок...</p>
          </div>
        </div>
      ) : (
        <div className="table-container">
          {mailings.length === 0 ? (
            <div className="empty-state">
              <p>Нет созданных рассылок</p>
              <button
                onClick={loadExampleMailing}
                className="btn btn-outline"
                style={{ marginTop: '1rem' }}
              >
                Показать пример рассылки
              </button>
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Название</th>
                  <th>Статус</th>
                  <th>Целевые интересы</th>
                  <th>Получателей</th>
                  <th>Отправлено</th>
                  <th>Прогресс</th>
                  <th
                    onClick={() => {
                      if (sortField === 'created_at') {
                        setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
                      } else {
                        setSortField('created_at');
                        setSortOrder('desc');
                      }
                    }}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Дата создания
                  </th>
                  <th
                    onClick={() => {
                      if (sortField === 'started_at') {
                        setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
                      } else {
                        setSortField('started_at');
                        setSortOrder('desc');
                      }
                    }}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Дата запуска
                  </th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {sortedMailings.map((mailing) => (
                  <tr key={mailing.id}>
                    <td className="text-sm font-medium text-gray-900">
                      {mailing.name}
                    </td>
                    <td>
                      {getStatusBadge(mailing.status)}
                    </td>
                    <td className="text-sm text-gray-900">
                      {(() => {
                        const targetClasses = mailing.target_classes;
                        // Специальные случаи
                        // if (mailing.target_classes.length === 0) {
                        //   return 'Без интереса';
                        // }
                        if (targetClasses.length === 0) {
                          return 'Все пользователи / Все категории';
                        }
                        if (targetClasses.includes(0) && targetClasses.length === 1) {
                          return 'Без интереса';
                        }
                        if (targetClasses.includes(0)) {
                          return 'Без интереса + ' + targetClasses.filter(id => id !== 0).map(getClassName).join(', ');
                        }
                        // Обычные классы
                        return mailing.target_classes.map(classId => getClassName(classId)).join(', ');
                      })()}
                    </td>
                    <td className="text-sm text-gray-900">
                      {mailing.recipients_count}
                    </td>
                    <td className="text-sm">
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <span style={{ color: '#16a34a', fontWeight: 500 }}>
                          Отправлено {mailing.sent_count || 0}/{mailing.recipients_count || 0}
                        </span>
                        {mailing.failed_count > 0 && (
                          <Tooltip
                            content={
                              <div className="error-tooltip">
                                <h4>Ошибки отправки ({mailing.failed_count})</h4>
                                {mailing.failed_details && mailing.failed_details.recent_errors && mailing.failed_details.recent_errors.length > 0 ? (
                                  <div className="error-details">
                                    {(() => {
                                      const groupedErrors = mailing.failed_details.recent_errors.reduce((acc, error) => {
                                        const errorMsg = translateError(error.error || 'Неизвестная ошибка');
                                        if (!acc[errorMsg]) {
                                          acc[errorMsg] = {
                                            message: errorMsg,
                                            telegram_ids: [],
                                            times: []
                                          };
                                        }
                                        acc[errorMsg].telegram_ids.push(error.telegram_id || 'Неизвестно');
                                        acc[errorMsg].times.push(error.time);
                                        return acc;
                                      }, {});

                                      return Object.values(groupedErrors).map((group, idx) => (
                                        <div key={idx} className="error-item">
                                          <span className="error-msg">{group.message}</span>
                                          <span className="error-user">TG ID: {group.telegram_ids.join(', ')}</span>
                                          <span className="error-msg" style={{ fontSize: '11px', color: '#9ca3af' }}>
                                            {new Date(group.times[0]).toLocaleString('ru-RU')}
                                          </span>
                                        </div>
                                      ));
                                    })()}
                                  </div>
                                ) : (
                                  <p>Детали ошибок недоступны</p>
                                )}
                              </div>
                            }
                          >
                            <span style={{ color: '#dc2626', fontWeight: 500, cursor: 'pointer' }}>
                              Не отправлено {mailing.failed_count}/{mailing.recipients_count || 0}
                            </span>
                          </Tooltip>
                        )}
                      </div>
                    </td>
                    <td className="text-sm text-gray-900">
                      <div className="flex items-center gap-2">
                        <div className="progress-bar">
                          <div
                            className="progress-bar-fill"
                            style={{ width: `${mailing.progress || 0}%` }}
                          ></div>
                        </div>
                        <span>{mailing.progress || 0}%</span>
                      </div>
                    </td>
                    <td className="text-sm text-gray-500">
                      {formatDate(mailing.created_at)}
                    </td>
                    <td className="text-sm text-gray-500">
                      {formatDate(mailing.started_at || mailing.scheduled_at)}
                    </td>
                    <td>
                      <div className="dropdown">
                        <button className="btn btn-small btn-outline">⋮</button>
                        <div className="dropdown-menu">
                          <button
                            onClick={() => handleEdit(mailing)}
                            className="dropdown-item"
                          >
                            Редактировать
                          </button>
                          {(mailing.status === 'draft' || mailing.status === 'scheduled') && (
                            <button
                              onClick={() => handleLaunch(mailing.id)}
                              className="dropdown-item"
                            >
                              Запустить
                            </button>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <Modal
        isOpen={showModal}
        onClose={closeModal}
        title={editingMailing ? "Редактировать рассылку" : "Создать рассылку"}
        size="large"
      >
        <div className="mailing-form">
          <div className="form-group">
            <label className="form-label">Название рассылки *</label>
            <input
              type="text"
              className="form-input"
              placeholder="Например: Новости недели"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              Текст сообщения *
              <Tooltip
                content={
                  <div className="tooltip-format-content">
                    <h4>📝 Форматирование текста</h4>
                    <p><span className="tooltip-label">Переносы строк:</span> просто нажмите Enter</p>
                    <p><span className="tooltip-label">Жирный:</span> &lt;b&gt;текст&lt;/b&gt; или &lt;strong&gt;текст&lt;/strong&gt;</p>
                    <p><span className="tooltip-label">Курсив:</span> &lt;i&gt;текст&lt;/i&gt; или &lt;em&gt;текст&lt;/em&gt;</p>
                    <p><span className="tooltip-label">Подчеркнутый:</span> &lt;u&gt;текст&lt;/u&gt;</p>
                    <p><span className="tooltip-label">Зачеркнутый:</span> &lt;s&gt;текст&lt;/s&gt; или &lt;del&gt;текст&lt;/del&gt;</p>
                    <p><span className="tooltip-label">Моноширинный:</span> &lt;code&gt;текст&lt;/code&gt;</p>
                    <p><span className="tooltip-label">Ссылка:</span> &lt;a href="https://example.com"&gt;текст&lt;/a&gt;</p>
                  </div>
                }
              >
                <span style={{ marginLeft: '8px', fontSize: '16px', color: '#6b7280' }}>ℹ️</span>
              </Tooltip>
            </label>
            <textarea
              className="form-textarea"
              rows="6"
              placeholder="Введите текст рассылки..."
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            />
            <div className="character-counter">
              <span className={formData.message.length > (formData.image ? 1024 : 4096) ? 'counter-exceeded' : ''}>
                {formData.message.length} / {formData.image ? '1024' : '4096'} символов
              </span>
              {formData.image && (
                <span className="counter-hint">С изображением лимит 1024 символа</span>
              )}
              {!formData.image && (
                <span className="counter-hint">Без изображения лимит 4096 символов</span>
              )}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Изображение (опционально)</label>
            <div className="image-upload-wrapper">
              <input
                type="file"
                id="image-upload"
                className="image-upload-input"
                accept=".jpg,.jpeg,.png,.gif,.webp"
                onChange={handleImageUpload}
              />
              <label htmlFor="image-upload" className="image-upload-button">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 4.167v11.666M4.167 10h11.666" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                {formData.image instanceof File ? 'Изменить изображение' : 'Выбрать изображение'}
              </label>
              <div className="image-upload-hint">
                Форматы: JPG, JPEG, PNG, GIF, WEBP • Макс. размер: 10 МБ
              </div>
            </div>
            {formData.image instanceof File && (
              <div className="image-preview-info">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 2h4a1 1 0 011 1v1h2.5a.5.5 0 010 1h-.441l-.443 8.863A1 1 0 0111.623 15H4.377a1 1 0 01-.993-.863L2.941 5H2.5a.5.5 0 010-1H5V3a1 1 0 011-1zm5 2H5v-.5a.5.5 0 01.5-.5h5a.5.5 0 01.5.5V4zm-7.441 1h8.882l-.438 8.775a.25.25 0 01-.248.225H4.245a.25.25 0 01-.248-.225L3.559 5z" fill="currentColor"/>
                </svg>
                <span className="image-name">{formData.imageName || formData.image.name}</span>
                <button
                  type="button"
                  className="image-remove-button"
                  onClick={() => {
                    if (formData.imageUrl) {
                      URL.revokeObjectURL(formData.imageUrl);
                    }
                    setFormData(prev => ({
                      ...prev,
                      image: null,
                      imageUrl: null,
                      imageName: ''
                    }));
                    document.getElementById('image-upload').value = '';
                  }}
                >
                  Удалить
                </button>
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Целевые интересы *</label>
            <div className="classes-selector">
              {classes.length === 0 ? (
                <p className="text-sm text-gray-500">Загрузка интересов...</p>
              ) : (
                <>
                  <div className="classes-grid">
                    {/* ✅ Виртуальные */}
                    {virtualClasses.map(cls => (
                      <label key={cls.id} className="class-checkbox">
                        <input
                          type="checkbox"
                          checked={formData.selectedClasses.includes(cls.id)}
                          onChange={() => handleClassToggle(cls.id)}
                        />
                        <span>{cls.name} <span className="text-gray-500">({cls.count})</span></span>
                      </label>
                    ))}
                    
                    {/* ✅ Реальные классы */}
                    {classes.map(cls => (
                      <label key={cls.id} className="class-checkbox">
                        <input
                          type="checkbox"
                          checked={formData.selectedClasses.includes(cls.id)}
                          onChange={() => handleClassToggle(cls.id)}
                        />
                        <span>{cls.name} <span className="text-gray-500">({stats.by_class[cls.id]?.count || 0})</span></span>
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {formData.selectedClasses.length > 0 && (
            <div className="recipients-counter">
              <span className="text-lg font-semibold">
                Получателей: {recipientsCount} человек
              </span>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Действие *</label>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  name="action"
                  value="draft"
                  checked={formData.action === 'draft'}
                  onChange={(e) => setFormData({ ...formData, action: e.target.value })}
                />
                <span>Сохранить как черновик</span>
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="action"
                  value="now"
                  checked={formData.action === 'now'}
                  onChange={(e) => setFormData({ ...formData, action: e.target.value })}
                />
                <span>Запустить сейчас</span>
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="action"
                  value="scheduled"
                  checked={formData.action === 'scheduled'}
                  onChange={(e) => setFormData({ ...formData, action: e.target.value })}
                />
                <span>Запланировать запуск</span>
              </label>
            </div>
          </div>

          {formData.action === 'scheduled' && (
            <div className="form-group">
              <label className="form-label">Дата и время запуска</label>
              <input
                type="datetime-local"
                className="form-input"
                value={formData.scheduledDate}
                onChange={(e) => setFormData({ ...formData, scheduledDate: e.target.value })}
                min={(() => {
                  const d = new Date();
                  const pad = (n) => String(n).padStart(2, '0');
                  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
                })()}
                style={
                  formData.scheduledDate && new Date(`${formData.scheduledDate}:00Z`) <= new Date()
                    ? { borderColor: '#e53935', boxShadow: '0 0 0 1px #e53935' }
                    : undefined
                }
              />
              {formData.scheduledDate && new Date(`${formData.scheduledDate}:00Z`) <= new Date() && (
                <div style={{ color: '#e53935', fontSize: 12, marginTop: 4 }}>
                  Нельзя запланировать рассылку на прошедшее время
                </div>
              )}
            </div>
          )}

          <div className="modal-actions">
            <button onClick={closeModal} className="btn btn-outline">
              Отмена
            </button>
            <button
              onClick={handleSubmit}
              className="btn btn-primary"
              disabled={
                formData.action === 'scheduled' &&
                (!formData.scheduledDate || new Date(`${formData.scheduledDate}:00Z`) <= new Date())
              }
            >
              {editingMailing ? 'Сохранить' : 'Создать'}
            </button>
          </div>
        </div>
      </Modal>

      <PreviewModal
        isOpen={showPreviewModal}
        onClose={handlePreviewCancel}
        onConfirm={handlePreviewConfirm}
        mailingData={previewData}
      />
    </div>
  );
}

export default Mailings;
